import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Tag, Spin, message, Modal, Form, Input,
    Select, Switch, Row, Col, Tabs, Tooltip, Popconfirm,
    Card, Space, Avatar, Checkbox, Badge, Divider,
} from 'antd';
import {
    LockOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
    SaveOutlined, TeamOutlined, AppstoreOutlined,
    CheckCircleFilled, CloseCircleFilled, ReloadOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';

const { Option } = Select;

// ── Group definitions — must stay in sync with modules.group_id ──────────────
const GROUP_MAP = {
    1: { label: 'Administration',    color: '#3b82f6', bg: '#eff6ff' },
    2: { label: 'Assets & Stock',    color: '#f59e0b', bg: '#fffbeb' },
    3: { label: 'Tickets & Jobs',    color: '#8b5cf6', bg: '#f5f3ff' },
    4: { label: 'Quality of Care',   color: '#16a34a', bg: '#f0fdf4' },
};
const TYPE_LABELS = {
    CLIENT: { label: 'Facility Users', color: 'blue'  },
    ADMIN:  { label: 'Admin Only',     color: 'red'   },
    BOTH:   { label: 'All Users',      color: 'green' },
};
const ROLE_COLORS = ['#6366f1','#0ea5e9','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899'];
const roleColor   = id => ROLE_COLORS[(parseInt(id) || 0) % ROLE_COLORS.length];

// ─────────────────────────────────────────────────────────────────────────────
export default function QoCAccessControl() {
    const [tab, setTab]         = useState('roles');

    // ── Data ─────────────────────────────────────────────────────────────────
    const [allModules, setAllModules] = useState([]);  // full modules table
    const [roles,      setRoles]      = useState([]);  // roles with their app_modules
    const [loading,    setLoading]    = useState(false);

    // ── Module CRUD modal ─────────────────────────────────────────────────────
    const [modModal,   setModModal]   = useState(false);
    const [modEdit,    setModEdit]    = useState(null);
    const [modForm]                   = Form.useForm();
    const [modSaving,  setModSaving]  = useState(false);

    // ── Role permissions modal ────────────────────────────────────────────────
    const [roleModal,  setRoleModal]  = useState(false);
    const [roleEdit,   setRoleEdit]   = useState(null);   // the role being edited
    const [rolePerms,  setRolePerms]  = useState([]);     // array of enabled module keys
    const [roleSaving, setRoleSaving] = useState(false);

    // ─── Load ──────────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [mRes, rRes] = await Promise.all([
                axios.get('/modules'),
                axios.get('/roles/ALL'),
            ]);
            setAllModules(mRes.data?.data || []);
            const rData = rRes.data?.result?.data || rRes.data?.data || [];
            setRoles(rData);
        } catch (e) {
            message.error('Failed to load data');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    // ─── Group modules for display ─────────────────────────────────────────────
    const grouped = {};
    allModules.forEach(m => {
        const gid = String(m.group_id);
        if (!grouped[gid]) grouped[gid] = [];
        grouped[gid].push(m);
    });

    // ─── Open role permissions ─────────────────────────────────────────────────
    const openRole = (role) => {
        // app_modules is stored as comma-separated string of module keys
        const enabled = role.app_modules
            ? (Array.isArray(role.app_modules)
                ? role.app_modules.map(k => String(k).trim()).filter(Boolean)
                : String(role.app_modules).split(',').map(k => k.trim()).filter(Boolean))
            : [];
        setRoleEdit(role);
        setRolePerms(enabled);
        setRoleModal(true);
    };

    const toggleModKey = (key, checked) => {
        setRolePerms(prev =>
            checked ? [...prev, key] : prev.filter(k => k !== key)
        );
    };

    const saveRolePerms = async () => {
        setRoleSaving(true);
        try {
            // Use the existing saveRole endpoint — matches User.php::saveRole()
            // It expects: id, title, status, app_modules (array → imploded to CSV)
            await axios.post('/saveRole', {
                id:          roleEdit.id,
                title:       roleEdit.role_name || roleEdit.title,
                status:      roleEdit.status ?? 1,
                app_modules: rolePerms,   // send as array; PHP does implode(",", $data['app_modules'])
            });
            message.success('Permissions saved — users must log out and back in for changes to apply');
            setRoleModal(false);
            load();
        } catch (e) {
            message.error('Save failed: ' + (e?.response?.data?.message || e.message));
        }
        setRoleSaving(false);
    };

    // ─── Module CRUD ───────────────────────────────────────────────────────────
    const openModModal = (mod = null) => {
        setModEdit(mod);
        modForm.setFieldsValue(mod ? {
            key:         mod.key,
            name:        mod.name,
            group_id:    parseInt(mod.group_id),
            type:        mod.type,
            is_national: parseInt(mod.is_national) === 1,
            status:      parseInt(mod.status) === 1,
        } : {
            group_id: 4, type: 'BOTH', is_national: false, status: true,
        });
        setModModal(true);
    };

    const saveModule = async () => {
        try {
            const vals = await modForm.validateFields();
            setModSaving(true);
            await axios.post('/saveModule', {
                ...vals,
                is_national: vals.is_national ? 1 : 0,
                status:      vals.status      ? 1 : 0,
                ...(modEdit ? { id: modEdit.id } : {}),
            });
            message.success(modEdit ? 'Module updated' : 'Module created');
            setModModal(false);
            load();
        } catch (e) {
            if (e?.errorFields) return;
            message.error('Save failed: ' + (e?.response?.data?.message || e.message));
        }
        setModSaving(false);
    };

    const deleteModule = async (id) => {
        try {
            await axios.post('/deleteModule', { id });
            message.success('Module deleted');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Delete failed');
        }
    };

    // ─── Module table columns ──────────────────────────────────────────────────
    const modCols = [
        {
            title: 'Module',
            key: 'module',
            render: (_, m) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{m.name}</div>
                    <code style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>
                        {m.key}
                    </code>
                </div>
            ),
        },
        {
            title: 'Group', dataIndex: 'group_id', key: 'group_id', width: 160,
            render: gid => {
                const g = GROUP_MAP[gid];
                return g
                    ? <Tag color={g.color} style={{ fontWeight: 500 }}>{g.label}</Tag>
                    : <Tag>Group {gid}</Tag>;
            },
            filters: Object.entries(GROUP_MAP).map(([k, v]) => ({ text: v.label, value: parseInt(k) })),
            onFilter: (val, r) => parseInt(r.group_id) === val,
        },
        {
            title: 'Applies To', dataIndex: 'type', key: 'type', width: 140,
            render: t => {
                const info = TYPE_LABELS[t] || { label: t, color: 'default' };
                return <Tag color={info.color}>{info.label}</Tag>;
            },
        },
        {
            title: 'National', dataIndex: 'is_national', key: 'is_national', width: 90, align: 'center',
            render: v => parseInt(v) === 1
                ? <CheckCircleFilled style={{ color: '#16a34a' }} />
                : <CloseCircleFilled style={{ color: '#d1d5db' }} />,
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status', width: 90, align: 'center',
            render: v => <Tag color={parseInt(v) === 1 ? 'success' : 'default'}>
                {parseInt(v) === 1 ? 'Active' : 'Inactive'}
            </Tag>,
        },
        {
            title: '', key: 'actions', width: 90, align: 'right',
            render: (_, m) => (
                <Space>
                    <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openModModal(m)} /></Tooltip>
                    <Popconfirm
                        title={parseInt(m.id) < 200
                            ? 'Built-in modules cannot be deleted. Deactivate instead.'
                            : 'Delete this module?'}
                        onConfirm={() => parseInt(m.id) >= 200 && deleteModule(m.id)}
                        icon={<ExclamationCircleOutlined style={{ color: '#ef4444' }} />}
                        okButtonProps={{ disabled: parseInt(m.id) < 200 }}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={parseInt(m.id) < 200} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ─── Role card — shows how many modules enabled ────────────────────────────
    const countEnabled = (role) => {
        if (!role.app_modules) return 0;
        // app_modules can be a string (CSV) or array depending on API
        const mods = Array.isArray(role.app_modules)
            ? role.app_modules
            : String(role.app_modules).split(',');
        return mods.filter(k => String(k).trim()).length;
    };

    return (
        <div className="page-content">
        <div style={{ padding: 24, maxWidth: 1200 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'linear-gradient(135deg,#7c3aed,#6366f1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <LockOutlined style={{ color: '#fff', fontSize: 20 }} />
                </div>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Access Control</h2>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        Manage module permissions per role, and create new modules
                    </p>
                </div>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ marginLeft: 'auto' }}>
                    Refresh
                </Button>
            </div>

            <Tabs activeKey={tab} onChange={setTab} type="card">

                {/* ── TAB 1: Role Permissions ───────────────────────────── */}
                <Tabs.TabPane tab={<span><TeamOutlined /> Role Permissions</span>} key="roles">
                    <div style={{
                        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
                        padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#1e40af',
                    }}>
                        ℹ️ Click a role to configure which modules its users can access.
                        Changes take effect on the user's <strong>next login</strong> — existing sessions are not affected.
                    </div>
                    <Spin spinning={loading}>
                        <Row gutter={[16, 16]}>
                            {roles.map(role => (
                                <Col xs={24} sm={12} md={8} key={role.id}>
                                    <Card
                                        hoverable
                                        onClick={() => openRole(role)}
                                        style={{ borderRadius: 12, cursor: 'pointer', borderColor: '#e2e8f0' }}
                                        bodyStyle={{ padding: '16px 20px' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Avatar size={40} style={{ background: roleColor(role.id), fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                                                {(role.role_name || role.title || '?').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                                                    {role.role_name || role.title}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                                    {role.user_count || 0} users
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Badge
                                                    count={countEnabled(role)}
                                                    style={{ background: roleColor(role.id) }}
                                                    overflowCount={99}
                                                    showZero
                                                />
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>modules</div>
                                            </div>
                                        </div>
                                        {/* Mini module preview */}
                                        {role.app_modules && (
                                            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {(Array.isArray(role.app_modules) ? role.app_modules : String(role.app_modules || '').split(',')).filter(k => k.trim()).slice(0, 5).map(k => (
                                                    <Tag key={k} style={{ fontSize: 10, margin: 0, padding: '0 5px' }}>{k.trim()}</Tag>
                                                ))}
                                                {(Array.isArray(role.app_modules) ? role.app_modules : String(role.app_modules || '').split(',')).filter(k => k.trim()).length > 5 && (
                                                    <Tag style={{ fontSize: 10, margin: 0, padding: '0 5px', background: '#f1f5f9' }}>
                                                        +{(Array.isArray(role.app_modules) ? role.app_modules : String(role.app_modules || '').split(',')).filter(k => k.trim()).length - 5}
                                                    </Tag>
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Spin>
                </Tabs.TabPane>

                {/* ── TAB 2: Module Management ──────────────────────────── */}
                <Tabs.TabPane tab={<span><AppstoreOutlined /> Module Management</span>} key="modules">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModModal()}
                            style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
                            New Module
                        </Button>
                    </div>
                    <Spin spinning={loading}>
                        <Table
                            dataSource={allModules} columns={modCols} rowKey="id"
                            size="middle" pagination={{ pageSize: 25, showSizeChanger: false }}
                        />
                    </Spin>
                </Tabs.TabPane>
            </Tabs>

            {/* ── Role Permissions Modal ─────────────────────────────────── */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar size={32}
                            style={{ background: roleEdit ? roleColor(roleEdit.id) : '#6366f1', fontWeight: 700 }}>
                            {(roleEdit?.role_name || roleEdit?.title || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <span>Permissions — {roleEdit?.role_name || roleEdit?.title}</span>
                        <Tag color="blue" style={{ marginLeft: 4 }}>
                            {rolePerms.length} enabled
                        </Tag>
                    </div>
                }
                open={roleModal}
                onCancel={() => setRoleModal(false)}
                width={700}
                footer={[
                    <Button key="all" onClick={() => setRolePerms(allModules.filter(m => parseInt(m.status) === 1).map(m => m.key))}>
                        Enable All
                    </Button>,
                    <Button key="none" onClick={() => setRolePerms([])}>
                        Disable All
                    </Button>,
                    <Button key="cancel" onClick={() => setRoleModal(false)}>Cancel</Button>,
                    <Button key="save" type="primary" loading={roleSaving} icon={<SaveOutlined />} onClick={saveRolePerms}
                        style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                        Save Permissions
                    </Button>,
                ]}
            >
                <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
                    Toggle the modules this role can access. Users see only navigation items they have permission for.
                </p>

                {Object.entries(grouped).sort(([a],[b]) => parseInt(a) - parseInt(b)).map(([gid, mods]) => {
                    const g = GROUP_MAP[gid] || { label: `Group ${gid}`, color: '#6b7280', bg: '#f9fafb' };
                    const activeMods = mods.filter(m => parseInt(m.status) === 1);
                    if (!activeMods.length) return null;

                    const groupEnabled = activeMods.filter(m => rolePerms.includes(m.key)).length;

                    return (
                        <div key={gid} style={{ marginBottom: 20 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                marginBottom: 10, paddingBottom: 8,
                                borderBottom: `2px solid ${g.color}33`,
                            }}>
                                <div style={{
                                    width: 8, height: 8, borderRadius: '50%', background: g.color, flexShrink: 0,
                                }} />
                                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: g.color }}>
                                    {g.label}
                                </span>
                                <Tag style={{ marginLeft: 'auto', fontSize: 10 }} color={groupEnabled > 0 ? 'success' : 'default'}>
                                    {groupEnabled}/{activeMods.length} enabled
                                </Tag>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {activeMods.map(m => {
                                    const enabled = rolePerms.includes(m.key);
                                    return (
                                        <div
                                            key={m.key}
                                            onClick={() => toggleModKey(m.key, !enabled)}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '9px 14px', borderRadius: 8, cursor: 'pointer',
                                                background: enabled ? g.bg : '#f8fafc',
                                                border: `1px solid ${enabled ? g.color + '44' : '#e2e8f0'}`,
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: enabled ? g.color : '#374151' }}>
                                                    {m.name}
                                                </div>
                                                <code style={{ fontSize: 10, color: '#94a3b8' }}>{m.key}</code>
                                                {parseInt(m.is_national) === 1 && (
                                                    <Tag color="orange" style={{ fontSize: 9, padding: '0 4px', marginLeft: 4 }}>national</Tag>
                                                )}
                                            </div>
                                            <Switch
                                                size="small"
                                                checked={enabled}
                                                onChange={v => { toggleModKey(m.key, v); }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                <Divider style={{ margin: '12px 0 6px' }} />
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    Saving will update this role's permissions immediately.
                    Affected users must log out and back in to see changes.
                </div>
            </Modal>

            {/* ── Module Create/Edit Modal ───────────────────────────────── */}
            <Modal
                title={modEdit ? 'Edit Module' : 'Create New Module'}
                open={modModal}
                onCancel={() => setModModal(false)}
                footer={[
                    <Button key="cancel" onClick={() => setModModal(false)}>Cancel</Button>,
                    <Button key="save" type="primary" loading={modSaving} icon={<SaveOutlined />} onClick={saveModule}
                        style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
                        {modEdit ? 'Save Changes' : 'Create Module'}
                    </Button>,
                ]}
                width={540}
            >
                <Form form={modForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Module Key" name="key"
                                rules={[
                                    { required: true, message: 'Required' },
                                    { pattern: /^[a-z0-9_]+$/, message: 'Lowercase, numbers, underscores only' },
                                ]}
                                help={modEdit ? 'Cannot change key of existing module' : 'e.g. qoc_reports'}
                            >
                                <Input placeholder="e.g. qoc_reports" disabled={!!modEdit} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Display Name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. QoC Reports" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Group" name="group_id" rules={[{ required: true }]}>
                                <Select>
                                    {Object.entries(GROUP_MAP).map(([k, v]) => (
                                        <Option key={k} value={parseInt(k)}>{v.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Applies To" name="type" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="CLIENT">Facility Users</Option>
                                    <Option value="ADMIN">Admin Only</Option>
                                    <Option value="BOTH">All Users</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="National Level Only" name="is_national" valuePropName="checked">
                                <Switch checkedChildren="Yes" unCheckedChildren="No" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Active" name="status" valuePropName="checked">
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
        </div>
    );
}